import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { CalendarDays, CircleHelp, Compass, Fish, History, Menu, X } from "lucide-react";
import PlannedTrips from "./PlannedTrips";
import TripSuggestions from "./TripSuggestions";
import InfoPopup from "./InfoPopup";
import { useTranslation } from "react-i18next";
import "../styles/components/trips/MyTripsShell.css";
import "../styles/components/trips/TripDetailsPanel.css";
import "../styles/components/trips/PastTripSummaryPanel.css";
import "../styles/components/trips/PastTripCatchModal.css";
import "../styles/components/trips/MyTripsLearnMoreModal.css";
import API_BASE, { withApiCredentials } from "../api";
import PastTripCatchModal from "./Trips/PastTripCatchModal";
import MyTripsLearnMoreModal from "./Trips/MyTripsLearnMoreModal";
import TripDetailsPanel from "./Trips/TripDetailsPanel";
import { DEFAULT_PAST_ANALYTICS_FILTERS, parseTripDate } from "./Trips/pastTripAnalytics";
import {
  getCatchSubmitRefreshEventName,
  reportBackgroundRefreshBlocked,
  shouldBlockBackgroundRefresh,
} from "../app/catchSubmitGate";
import { trackProductEvent } from "../analytics/trackEvent";
import useLocationStateStack from "../app/useLocationStateStack";
import { showExploreTrips } from "../config/featureVisibility";
import AppBackButton from "./common/AppBackButton";
import FeedMobileBottomNav from "./FeedMobileBottomNav";
import DesktopRightRail from "./DesktopRightRail";

const PAST_TRIP_CATCH_MAP_DRAFT_KEY =
  "reelitpro:past-trip-catch-map-draft";
const PAST_TRIP_CATCH_MAP_RETURN_KEY =
  "reelitpro:past-trip-catch-map-return";

function readSessionJson(key) {
  if (typeof window === "undefined") return null;

  try {
    const rawValue = window.sessionStorage.getItem(key);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch {
    return null;
  }
}

function writeSessionJson(key, value) {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage failures; the picker flow can still fall back gracefully.
  }
}

function removeSessionItem(key) {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // no-op
  }
}

function resolveBucketToday(serverToday) {
  const parsedServerToday = parseTripDate(serverToday);
  if (parsedServerToday) return parsedServerToday;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

function sortTripsByTime(trips, serverToday) {
  const today = resolveBucketToday(serverToday);

  const planned = [];
  const current = [];
  const past = [];

  trips.forEach((trip) => {
    if (!trip.startDate) {
      planned.push(trip);
      return;
    }

    const start = parseTripDate(trip.startDate);
    const end = trip.endDate ? parseTripDate(trip.endDate) : start;

    if (!start || !end) {
      planned.push(trip);
      return;
    }

    if (end < today) {
      past.push(trip);
    } else if (start > today) {
      planned.push(trip);
    } else {
      current.push(trip);
    }
  });

  planned.sort((a, b) => {
    const first = parseTripDate(a.startDate);
    const second = parseTripDate(b.startDate);
    return (first?.getTime() ?? 0) - (second?.getTime() ?? 0);
  });
  current.sort((a, b) => {
    const first = parseTripDate(a.endDate || a.startDate);
    const second = parseTripDate(b.endDate || b.startDate);
    return (first?.getTime() ?? 0) - (second?.getTime() ?? 0);
  });
  past.sort((a, b) => {
    const first = parseTripDate(a.endDate || a.startDate);
    const second = parseTripDate(b.endDate || b.startDate);
    return (second?.getTime() ?? 0) - (first?.getTime() ?? 0);
  });

  return { planned, current, past };
}

function withTripApiCredentials(options = {}) {
  const token =
    typeof window !== "undefined" ? window.localStorage.getItem("token") : "";
  if (!token) {
    return withApiCredentials(options);
  }

  const headers = new Headers(options.headers || {});
  headers.set("Authorization", `Bearer ${token}`);
  return withApiCredentials({
    ...options,
    headers,
  });
}

export default function MyTrips({
  setShowTrips,
  setShowSidebar,
  user,
  posts = [],
  users = [],
  onOpenUser,
  onRootTabSelect,
  onSidebarRouteNavigate,
  enableDesktopRightRail = false,
  desktopRightRailTargetId,
  showBackButton = true,
  useShellChrome = false,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const backLabel = t("back", { defaultValue: "Back" }).replace(
    /^[<\-\u2013\u2014\u2190\u2B05\s]+/,
    ""
  );
  const userId = user?.id;

  const [fishData, setFishData] = useState(null);
  const [panel, setPanel] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [weatherConditions, setWeatherConditions] = useState(null);
  const [fishingMethod, setFishingMethod] = useState(null);
  const [pastTripSummary, setPastTripSummary] = useState(null);
  const [pastTripSummaryLoading, setPastTripSummaryLoading] = useState(false);
  const [pastTripSummaryError, setPastTripSummaryError] = useState("");
  const [desktopRightRailTarget, setDesktopRightRailTarget] = useState(null);
  const [selectedSummaryPost, setSelectedSummaryPost] = useState(null);
  const [expandedSummaryMedia, setExpandedSummaryMedia] = useState(null);
  const [showLearnMore, setShowLearnMore] = useState(false);
  const [showPastTripCatchModal, setShowPastTripCatchModal] = useState(false);
  const [pastTripCatchDraft, setPastTripCatchDraft] = useState(null);
  const [pastTripCatchMapPick, setPastTripCatchMapPick] = useState(null);
  const [selectedWeatherDay, setSelectedWeatherDay] = useState(null);
  const [pastAnalyticsView, setPastAnalyticsView] = useState("day");
  const [pastAnalyticsFilters, setPastAnalyticsFilters] = useState(
    DEFAULT_PAST_ANALYTICS_FILTERS
  );
  const [bucketedTrips, setBucketedTrips] = useState({
    planned: [],
    current: [],
    past: [],
  });

  const refreshTripsRef = useRef(null);
  const skipPastTripCatchCloseRef = useRef(false);
  const restoredPastTripCatchPickRef = useRef("");

  useEffect(() => {
    if (!desktopRightRailTargetId || typeof document === "undefined") {
      setDesktopRightRailTarget(null);
      return;
    }

    setDesktopRightRailTarget(
      document.getElementById(desktopRightRailTargetId)
    );
  }, [desktopRightRailTargetId]);

  const clearTripsPanelState = useCallback(() => {
    setPanel(null);
    setSelectedTrip(null);
    setWeatherConditions(null);
    setFishingMethod(null);
    setPastTripSummary(null);
    setPastTripSummaryLoading(false);
    setPastTripSummaryError("");
    setSelectedSummaryPost(null);
    setExpandedSummaryMedia(null);
    setShowPastTripCatchModal(false);
    setPastTripCatchDraft(null);
    setPastTripCatchMapPick(null);
    setSelectedWeatherDay(null);
    setPastAnalyticsView("day");
    setPastAnalyticsFilters(DEFAULT_PAST_ANALYTICS_FILTERS);
  }, []);

  const closePanelLayer = () => clearTripsPanelState();
  const closeSelectedTrip = useCallback(() => {
    setSelectedTrip(null);
    setWeatherConditions(null);
    setSelectedWeatherDay(null);
  }, []);
  const closeSelectedSummaryPost = () => setSelectedSummaryPost(null);
  const closeExpandedSummaryMedia = () => setExpandedSummaryMedia(null);
  const closePastTripCatchModal = () => {
    setShowPastTripCatchModal(false);
    setPastTripCatchDraft(null);
    setPastTripCatchMapPick(null);
  };
  const closeSelectedWeatherDay = () => setSelectedWeatherDay(null);

  const tripsHistoryItems = [
    {
      id: "trips-panel",
      isOpen: Boolean(panel),
      close: closePanelLayer,
    },
    {
      id: "trips-selected-trip",
      isOpen: Boolean(panel && selectedTrip),
      close: closeSelectedTrip,
    },
    {
      id: "trips-weather-day",
      isOpen: Boolean(selectedWeatherDay),
      close: closeSelectedWeatherDay,
    },
    {
      id: "trips-summary-info",
      isOpen: Boolean(selectedSummaryPost),
      close: closeSelectedSummaryPost,
    },
    {
      id: "trips-summary-media",
      isOpen: Boolean(expandedSummaryMedia),
      close: closeExpandedSummaryMedia,
    },
    {
      id: "trips-add-catch",
      isOpen: panel === "past" && Boolean(selectedTrip) && showPastTripCatchModal,
      close: closePastTripCatchModal,
    },
  ];

  const {
    requestReplaceSync: requestTripsHistoryReplace,
    requestSuppressNavigationSync: requestTripsHistorySuppressNavigation,
  } = useLocationStateStack("tripsOverlayStack", tripsHistoryItems);

  const closeSelectedTripView = useCallback((event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    requestTripsHistoryReplace();
    closeSelectedTrip();
  }, [closeSelectedTrip, requestTripsHistoryReplace]);

  const closePanel = useCallback((event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    requestTripsHistoryReplace();
    clearTripsPanelState();
  }, [clearTripsPanelState, requestTripsHistoryReplace]);

  const hasActiveTripsPanelState = Boolean(
    panel ||
      selectedTrip ||
      selectedWeatherDay ||
      pastTripSummary ||
      pastTripSummaryLoading ||
      pastTripSummaryError ||
      selectedSummaryPost ||
      expandedSummaryMedia ||
      showPastTripCatchModal ||
      fishingMethod ||
      pastAnalyticsView !== "day" ||
      pastAnalyticsFilters !== DEFAULT_PAST_ANALYTICS_FILTERS
  );

  const handleBackClick = useCallback((event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (hasActiveTripsPanelState) {
      requestTripsHistoryReplace();
      clearTripsPanelState();
      return;
    }
    clearTripsPanelState();
    setShowTrips(false);
  }, [
    clearTripsPanelState,
    hasActiveTripsPanelState,
    requestTripsHistoryReplace,
    setShowTrips,
  ]);

  const fetchTripDetails = async (tripId) => {
    try {
      const res = await fetch(
        `${API_BASE}/trips/${tripId}?userId=${encodeURIComponent(userId)}`,
        withTripApiCredentials()
      );

      if (res.status === 401) {
        alert(t("trip_error_session_expired"));
        return;
      }

      if (!res.ok) throw new Error("Trip not found");

      const data = await res.json();
      setSelectedTrip(data);
      trackProductEvent("trip_opened", {
        featureArea: "trips",
        metadata: {
          source: "trip_list",
          panel: panel || "",
          has_dates: Boolean(data?.startDate),
        },
      });

      setWeatherConditions(null);
      try {
        const weatherRes = await fetch(
          `${API_BASE}/trips/${tripId}/conditions?userId=${encodeURIComponent(userId)}`,
          withTripApiCredentials()
        );
        if (weatherRes.ok) {
          const weatherData = await weatherRes.json();
          setWeatherConditions(weatherData);
        } else {
          console.error("Weather fetch failed status:", weatherRes.status);
        }
      } catch (wErr) {
        console.error("Weather fetch error:", wErr);
      }
    } catch (err) {
      console.error("Failed to load trip:", err);
    }
  };

  const refreshTrips = async ({ preserveSelection = false } = {}) => {
    if (shouldBlockBackgroundRefresh()) {
      reportBackgroundRefreshBlocked({ source: "trips_refresh" });
      return;
    }
    try {
      const res = await fetch(
        `${API_BASE}/trips/?userId=${encodeURIComponent(userId)}`,
        withTripApiCredentials()
      );

      if (res.status === 401) {
        alert(t("trip_error_session_expired"));
        return;
      }

      const data = await res.json();
      const serverToday = res.headers.get("X-Server-Today");
      const sorted = sortTripsByTime(data, serverToday);
      setBucketedTrips(sorted);

      if (!preserveSelection) {
        setSelectedTrip(null);
        setWeatherConditions(null);
      }
    } catch (err) {
      console.error("Failed to refresh trips:", err);
    }
  };
  refreshTripsRef.current = refreshTrips;

  useEffect(() => {
    const refreshEvent = getCatchSubmitRefreshEventName();
    const handleCatchSubmitRefresh = () => {
      refreshTripsRef.current?.({ preserveSelection: true });
    };
    window.addEventListener(refreshEvent, handleCatchSubmitRefresh);
    return () => {
      window.removeEventListener(refreshEvent, handleCatchSubmitRefresh);
    };
  }, []);

  useEffect(() => {
    if (!userId) return;

    fetch("/JSON/trip-data.json")
      .then((res) => res.json())
      .then(setFishData)
      .catch((err) => console.error("Error loading fish data:", err));

    async function loadTrips() {
      try {
        const res = await fetch(
          `${API_BASE}/trips/?userId=${encodeURIComponent(userId)}`,
          withTripApiCredentials()
        );

        if (res.status === 401) {
          alert(t("trip_error_session_expired"));
          return;
        }

        const data = await res.json();
        const serverToday = res.headers.get("X-Server-Today");
        const sorted = sortTripsByTime(data, serverToday);
        setBucketedTrips(sorted);
      } catch (err) {
        console.error("Failed to load trips:", err);
      }
    }

    loadTrips();
  }, [userId, t]);

  useEffect(() => {
    if (!userId) return undefined;

    let timeoutId;
    let cancelled = false;

    const scheduleMidnightRefresh = () => {
      const now = new Date();
      const nextMidnight = new Date(now);
      nextMidnight.setHours(24, 0, 1, 0);

      const delay = Math.max(nextMidnight.getTime() - now.getTime(), 1000);

      timeoutId = window.setTimeout(async () => {
        if (cancelled) return;

        await refreshTripsRef.current?.({ preserveSelection: true });

        if (!cancelled) {
          scheduleMidnightRefresh();
        }
      }, delay);
    };

    scheduleMidnightRefresh();

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [userId]);

  useEffect(() => {
    if (skipPastTripCatchCloseRef.current) {
      skipPastTripCatchCloseRef.current = false;
      return;
    }

    setShowPastTripCatchModal(false);
  }, [panel, selectedTrip?.id]);

  useEffect(() => {
    setSelectedWeatherDay(null);
  }, [panel, selectedTrip?.id]);

  useEffect(() => {
    if (!userId) return;

    const draftPayload = readSessionJson(PAST_TRIP_CATCH_MAP_DRAFT_KEY);
    const returnPayload = readSessionJson(PAST_TRIP_CATCH_MAP_RETURN_KEY);
    const coordinates =
      typeof returnPayload?.coordinates === "string"
        ? returnPayload.coordinates.trim()
        : "";

    if (!draftPayload || !returnPayload) return;

    const tripId = String(draftPayload.tripId || "").trim();
    const restoreKey = [
      tripId,
      returnPayload.selectionId || coordinates || returnPayload.requestedAt,
    ].join(":");
    if (!tripId || restoredPastTripCatchPickRef.current === restoreKey) return;

    const restoredTrip =
      bucketedTrips.past.find((trip) => String(trip?.id || "") === tripId) ||
      draftPayload.trip ||
      null;
    if (!restoredTrip) return;

    restoredPastTripCatchPickRef.current = restoreKey;
    removeSessionItem(PAST_TRIP_CATCH_MAP_DRAFT_KEY);
    removeSessionItem(PAST_TRIP_CATCH_MAP_RETURN_KEY);

    skipPastTripCatchCloseRef.current = true;
    setPanel("past");
    setSelectedTrip(restoredTrip);
    setPastTripCatchDraft(
      draftPayload.formState && typeof draftPayload.formState === "object"
        ? draftPayload.formState
        : null
    );
    setPastTripCatchMapPick(
      coordinates
        ? {
            coordinates,
            selectionId: returnPayload.selectionId || Date.now(),
          }
        : null
    );
    setShowPastTripCatchModal(true);
  }, [bucketedTrips.past, userId]);

  useEffect(() => {
    if (panel !== "past" || !selectedTrip?.id || !userId) {
      setPastTripSummary(null);
      setPastTripSummaryLoading(false);
      setPastTripSummaryError("");
      setSelectedSummaryPost(null);
      setExpandedSummaryMedia(null);
      setPastAnalyticsView("day");
      setPastAnalyticsFilters(DEFAULT_PAST_ANALYTICS_FILTERS);
      return undefined;
    }

    let cancelled = false;

    const loadPastTripSummary = async () => {
      setSelectedSummaryPost(null);
      setExpandedSummaryMedia(null);
      setPastAnalyticsView("day");
      setPastAnalyticsFilters(DEFAULT_PAST_ANALYTICS_FILTERS);
      setPastTripSummary(null);
      setPastTripSummaryError("");
      setPastTripSummaryLoading(true);

      try {
        const res = await fetch(
          `${API_BASE}/trips/${selectedTrip.id}/past-summary?userId=${encodeURIComponent(
            userId
          )}`,
          withTripApiCredentials()
        );

        if (cancelled) return;

        if (res.status === 401) {
          setPastTripSummaryError(t("trip_error_session_expired"));
          return;
        }

        if (!res.ok) {
          throw new Error(`Past trip summary failed: ${res.status}`);
        }

        const data = await res.json();
        if (!cancelled) {
          setPastTripSummary(data);
        }
      } catch (err) {
        console.error("Failed to load past trip summary:", err);
        if (!cancelled) {
          setPastTripSummaryError(
            t("trip_past_summary_error", {
              defaultValue: "Could not load trip catch summary.",
            })
          );
        }
      } finally {
        if (!cancelled) {
          setPastTripSummaryLoading(false);
        }
      }
    };

    loadPastTripSummary();

    return () => {
      cancelled = true;
    };
  }, [panel, selectedTrip?.id, selectedTrip?.updatedAt, userId, t]);

  const open = (key) => {
    if (key === "explore") {
      alert(t("trip_coming_soon"));
      return;
    }

    setPanel(key);
    trackProductEvent("trip_panel_opened", {
      featureArea: "trips",
      metadata: { panel: key, source: "my_trips" },
    });

    if (key === "planned") {
      setFishingMethod(null);
    }

    setSelectedTrip(null);
    setWeatherConditions(null);
  };

  const handleMobileRootTabSelect = useCallback(
    (nextTab) => {
      const normalizedTab = String(nextTab || "").trim().toLowerCase();
      if (!normalizedTab) return;

      requestTripsHistorySuppressNavigation();
      if (typeof onRootTabSelect === "function") {
        onRootTabSelect(normalizedTab);
        return;
      }

      navigate(
        normalizedTab === "feed"
          ? "/"
          : `/?tab=${encodeURIComponent(normalizedTab)}`
      );
    },
    [navigate, onRootTabSelect, requestTripsHistorySuppressNavigation]
  );

  const handleMobileRouteNavigate = useCallback(
    (path) => {
      if (typeof path !== "string" || !path.startsWith("/")) return;
      if (path !== "/trips") {
        requestTripsHistorySuppressNavigation();
        if (typeof onSidebarRouteNavigate === "function") {
          onSidebarRouteNavigate(path);
          return;
        }
      }

      navigate(path);
    },
    [navigate, onSidebarRouteNavigate, requestTripsHistorySuppressNavigation]
  );

  const getActiveList = () => {
    if (panel === "current") return bucketedTrips.current;
    if (panel === "past") return bucketedTrips.past;
    return bucketedTrips.planned;
  };

  const getPanelTitle = () => {
    if (panel === "current") return `\u{1F30A} ${t("trip_current")}`;
    if (panel === "past") return `\u{1F4DC} ${t("trip_past")}`;
    return `\u{1F4D3} ${t("planned_trips_title")}`;
  };

  const isPlannedDetailView = panel === "planned" && Boolean(selectedTrip);
  const isCurrentDetailView = panel === "current" && Boolean(selectedTrip);
  const isPastDetailView = panel === "past" && Boolean(selectedTrip);
  const showDesktopRightRail = enableDesktopRightRail && !panel;
  const desktopRightRail = showDesktopRightRail ? (
    <DesktopRightRail
      className="mytrips-desktop-right-rail"
      currentUser={user}
      posts={posts}
      users={users}
      onOpenUser={onOpenUser}
    />
  ) : null;
  const usesGenericTripDetailHeader =
    isPlannedDetailView || isCurrentDetailView || isPastDetailView;

  const getDetailHeaderMeta = () => {
    if (panel === "current") {
      return { Icon: Fish, label: t("trip_current") };
    }
    if (panel === "past") {
      return { Icon: History, label: t("trip_past_detail_title") };
    }
    return {
      Icon: CalendarDays,
      label: t("planned_trip_detail_title"),
    };
  };

  const detailHeaderMeta = getDetailHeaderMeta();
  const DetailHeaderIcon = detailHeaderMeta.Icon;

  const getEmptyMessage = () => {
    if (panel === "current") {
      return t("trip_current_empty", {
        defaultValue: "No trips happening right now. Go fishing!",
      });
    }
    if (panel === "past") {
      return t("trip_past_empty", {
        defaultValue: "No past trips recorded yet.",
      });
    }
    return null;
  };

  const summaryPosts = panel === "past" ? pastTripSummary?.items || [] : [];
  const mobileTripSummaryCards = [
    {
      key: "current",
      label: t("trip_current", { defaultValue: "Current" }),
      count: bucketedTrips.current.length,
      icon: Fish,
    },
    {
      key: "planned",
      label: t("trip_planned_short", { defaultValue: "Planned" }),
      count: bucketedTrips.planned.length,
      icon: CalendarDays,
    },
    {
      key: "past",
      label: t("trip_past_short", { defaultValue: "Past" }),
      count: bucketedTrips.past.length,
      icon: History,
    },
  ];

  const handlePastTripCatchSaved = async (updatedTrip) => {
    setShowPastTripCatchModal(false);
    setPastTripCatchDraft(null);
    setPastTripCatchMapPick(null);
    setSelectedTrip(updatedTrip);
    trackProductEvent("trip_catch_added", {
      featureArea: "trips",
      metadata: { source: "past_trip_catch_modal" },
    });
    await refreshTripsRef.current?.({ preserveSelection: true });
  };

  const handlePastTripCatchPickFromMap = useCallback(
    (formState) => {
      if (!selectedTrip?.id) return;

      writeSessionJson(PAST_TRIP_CATCH_MAP_DRAFT_KEY, {
        tripId: selectedTrip.id,
        trip: selectedTrip,
        formState,
        requestedAt: Date.now(),
      });
      requestTripsHistorySuppressNavigation();
      setShowPastTripCatchModal(false);

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("goToMapPicker", {
            detail: {
              source: "pastTripCatch",
            },
          })
        );
      }
    },
    [requestTripsHistorySuppressNavigation, selectedTrip]
  );

  if (!userId) {
    console.warn("MyTrips: no logged in user");
    return null;
  }

  return (
    <div
      className={`mytrips-container ${
        useShellChrome ? "" : "rp-shell rp-home-shell"
      } mytrips-mobile-shell ${
        useShellChrome ? "mytrips-shell-embedded" : ""
      } ${
        panel ? "mytrips-container--panel-open" : ""
      } ${
        showDesktopRightRail ? "mytrips-container--with-right-rail" : ""
      }`}
      style={{
        backgroundImage: "url(/fishing-spot-images/mahi-mahi-mytrips.jpg)",
      }}
    >
      {!useShellChrome ? (
        <div className="mytrips-mobile-topchrome rp-mobile-topchrome">
          <div className="mytrips-mobile-navbar rp-navbar">
            <button
              type="button"
              className="mytrips-mobile-topchrome__menu rp-navbar__menu-btn mobile-menu-btn"
              onClick={() => setShowSidebar?.((current) => !current)}
              aria-label={t("common_menu")}
            >
              <Menu size={23} strokeWidth={2.25} aria-hidden="true" />
            </button>

            <div className="mytrips-mobile-topchrome__logo rp-navbar__logo">
              <img
                src="/banner/logo.png"
                alt={t("app_title")}
                className="mytrips-mobile-topchrome__logo-img rp-navbar__logo-img"
              />
              <div className="mytrips-mobile-topchrome__wordmark rp-navbar__wordmark">
                {t("app_title")}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mytrips-header">
        <h2 className="mytrips-header-title">{"\u{1F9ED} "}{t("my_trips")}</h2>
      </div>

      <div className="mytrips-main">
        {showBackButton ? (
          <AppBackButton
            className="mytrips-back-btn"
            label={backLabel}
            onClick={handleBackClick}
          />
        ) : null}

        <div className="mytrips-mobile-page-head">
          <div className="mytrips-mobile-title-row">
            <span className="mytrips-mobile-title-icon" aria-hidden="true">
              <Compass size={21} strokeWidth={2.3} />
            </span>
            <h1>{t("my_trips", { defaultValue: "My Trips" })}</h1>
            <button
              type="button"
              className="mytrips-mobile-close-btn"
              aria-label={t("close", { defaultValue: "Close" })}
              onClick={handleBackClick}
            >
              <X size={21} strokeWidth={2.3} aria-hidden="true" />
            </button>
          </div>
          <p className="mytrips-mobile-subtitle">
            {t("my_trips_mobile_subtitle", {
              defaultValue: "Plan, track, and relive your fishing adventures.",
            })}
          </p>
        </div>

        <div className="mytrips-sidebar">
          <button className="mytrips-sidebar-btn" onClick={() => open("past")}>
            {t("trip_past")} ({bucketedTrips.past.length})
          </button>
          <button className="mytrips-sidebar-btn" onClick={() => open("current")}>
            {t("trip_current")} ({bucketedTrips.current.length})
          </button>
          <button className="mytrips-sidebar-btn" onClick={() => open("planned")}>
            {t("planned_trips_title")} ({bucketedTrips.planned.length})
          </button>
          {showExploreTrips ? (
            <button className="mytrips-sidebar-btn" onClick={() => alert(t("trip_coming_soon"))}>
              {t("trip_explore")}
            </button>
          ) : null}
        </div>

        <div className="mytrips-landing-content">
          <div className="mytrips-cta-wrapper">
            <button
              className="mytrips-main-btn mytrips-main-btn--desktop"
              onClick={() => open("plan")}
            >
              {"\u2795 "}{t("trip_plan_next")}
            </button>

            <button
              type="button"
              className="mytrips-plan-card"
              aria-label={t("trip_plan_next", { defaultValue: "Plan your next trip" })}
              onClick={() => open("plan")}
            >
              <img
                src="/banner/mytrips.png"
                alt=""
                aria-hidden="true"
                className="mytrips-plan-card__image mytrips-plan-card__image--light"
              />
              <img
                src="/banner/Darkmode/my-trips-hero-dark.png"
                alt=""
                aria-hidden="true"
                className="mytrips-plan-card__image mytrips-plan-card__image--dark"
              />
            </button>
          </div>

          <div
            className="mytrips-mobile-summary-grid"
            aria-label={t("trip_counts_aria_label", { defaultValue: "Trip counts" })}
          >
            {mobileTripSummaryCards.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  className="mytrips-summary-card"
                  onClick={() => open(item.key)}
                >
                  <span className="mytrips-summary-card__icon" aria-hidden="true">
                    <Icon size={17} strokeWidth={2.25} />
                  </span>
                  <span className="mytrips-summary-card__label">{item.label}</span>
                  <strong className="mytrips-summary-card__count">{item.count}</strong>
                  <span className="mytrips-summary-card__unit">
                    {t("trips", { defaultValue: "Trips" })}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mytrips-learn-more-entry">
            <button
              type="button"
              className="mytrips-learn-more-entry__button"
              onClick={() => setShowLearnMore(true)}
            >
              <CircleHelp size={18} strokeWidth={2.3} aria-hidden="true" />
              <span>{t("myTrips.learnMore.openButton")}</span>
            </button>
          </div>
        </div>

        {desktopRightRail && desktopRightRailTarget
          ? createPortal(desktopRightRail, desktopRightRailTarget)
          : desktopRightRail && !desktopRightRailTargetId
            ? desktopRightRail
            : null}

        {panel === "plan" && (
          <TripSuggestions
            onClose={closePanel}
            refreshTrips={refreshTrips}
            userId={userId}
          />
        )}

        {["planned", "current", "past"].includes(panel) && (
          <div
            className={`mytrips-planned-fullscreen ${
              selectedTrip ? "show-details" : ""
            } ${panel ? `mytrips-planned-fullscreen--${panel}` : ""} ${
              isPlannedDetailView ? "mytrips-planned-fullscreen--planned-detail" : ""
            } ${
              isCurrentDetailView ? "mytrips-planned-fullscreen--current-detail" : ""
            } ${
              isPastDetailView ? "mytrips-planned-fullscreen--past-detail" : ""
            }`}
          >
            <div className="mytrips-planned-header">
              <h2 className="mytrips-planned-header-title">
                {selectedTrip ? (
                  <>
                    <DetailHeaderIcon
                      size={19}
                      strokeWidth={2.35}
                      aria-hidden="true"
                    />
                    <span>{detailHeaderMeta.label}</span>
                  </>
                ) : (
                  getPanelTitle()
                )}
              </h2>
              <button
                type="button"
                className="mytrips-planned-close-btn"
                aria-label={t("trips_close_view_label", {
                  defaultValue: "Close trips view",
                })}
                onClick={handleBackClick}
              >
                {"\u00D7"}
              </button>
            </div>

            <div className="mytrips-planned-split">
              <div className="mytrips-planned-content">
                <PlannedTrips
                  trips={getActiveList()}
                  setSelectedTrip={(trip) => fetchTripDetails(trip.id)}
                  refreshTrips={refreshTrips}
                  customTitle={getPanelTitle()}
                  emptyMessage={getEmptyMessage()}
                  panel={panel}
                  onClose={handleBackClick}
                  closeLabel={t("trips_close_view_label", {
                    defaultValue: "Close trips view",
                  })}
                />
              </div>

              <div className="mytrips-details-panel">
                {selectedTrip ? (
                  <div className="planned-trips-mobile-intro mytrips-trip-detail-intro">
                    <span
                      className="planned-trips-mobile-intro__icon"
                      aria-hidden="true"
                    >
                      <DetailHeaderIcon size={19} strokeWidth={2.35} />
                    </span>
                    <div className="planned-trips-mobile-intro__copy">
                      <h3>
                        {usesGenericTripDetailHeader
                          ? detailHeaderMeta.label
                          : selectedTrip.name}
                      </h3>
                      <p>
                        {usesGenericTripDetailHeader
                          ? selectedTrip.location || detailHeaderMeta.label
                          : detailHeaderMeta.label}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="planned-trips-mobile-intro__close"
                      aria-label={t("trips_close_view_label")}
                      onClick={closeSelectedTripView}
                    >
                      <X size={18} strokeWidth={2.5} aria-hidden="true" />
                    </button>
                  </div>
                ) : null}
                <TripDetailsPanel
                  panel={panel}
                  selectedTrip={selectedTrip}
                  userId={userId}
                  fishData={fishData}
                  fishingMethod={fishingMethod}
                  weatherConditions={weatherConditions}
                  onRefreshWeather={setWeatherConditions}
                  setSelectedTrip={setSelectedTrip}
                  selectedWeatherDay={selectedWeatherDay}
                  onSelectedWeatherDayChange={setSelectedWeatherDay}
                  pastSummaryProps={{
                    summary: pastTripSummary,
                    loading: pastTripSummaryLoading,
                    error: pastTripSummaryError,
                    analyticsView: pastAnalyticsView,
                    setAnalyticsView: setPastAnalyticsView,
                    analyticsFilters: pastAnalyticsFilters,
                    setAnalyticsFilters: setPastAnalyticsFilters,
                    onOpenInfo: setSelectedSummaryPost,
                    onOpenMedia: setExpandedSummaryMedia,
                    onOpenAddCatch: () => {
                      setPastTripCatchDraft(null);
                      setPastTripCatchMapPick(null);
                      setShowPastTripCatchModal(true);
                    },
                  }}
                />
              </div>
            </div>

            {selectedSummaryPost && (
              <InfoPopup
                post={selectedSummaryPost}
                posts={summaryPosts}
                viewer={user}
                onClose={closeSelectedSummaryPost}
              />
            )}

            {panel === "past" && selectedTrip && showPastTripCatchModal && (
              <PastTripCatchModal
                trip={selectedTrip}
                userId={userId}
                initialFormState={pastTripCatchDraft}
                mapPickedCoordinates={pastTripCatchMapPick?.coordinates || ""}
                mapPickSelectionId={pastTripCatchMapPick?.selectionId || ""}
                onPickCoordinatesFromMap={handlePastTripCatchPickFromMap}
                onClose={closePastTripCatchModal}
                onSaved={handlePastTripCatchSaved}
              />
            )}
          </div>
        )}
      </div>

      {expandedSummaryMedia && (
        <div
          className="mytrips-past-media-overlay"
          onClick={closeExpandedSummaryMedia}
        >
          <button
            type="button"
            className="mytrips-past-media-close"
            onClick={(e) => {
              e.stopPropagation();
              closeExpandedSummaryMedia();
            }}
          >
            {"\u00D7"}
          </button>

          {expandedSummaryMedia.type === "video" ? (
            <video
              src={expandedSummaryMedia.src}
              controls
              autoPlay
              className="mytrips-past-media-expanded"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={expandedSummaryMedia.src}
              alt={expandedSummaryMedia.alt || t("post_media")}
              className="mytrips-past-media-expanded"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}

      {showLearnMore ? (
        <MyTripsLearnMoreModal onClose={() => setShowLearnMore(false)} />
      ) : null}

      {!useShellChrome ? (
        <FeedMobileBottomNav
          activeTab="my_trips"
          onTabSelect={handleMobileRootTabSelect}
          onRouteNavigate={handleMobileRouteNavigate}
        />
      ) : null}
    </div>
  );
}
